import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth, useTheme } from "@/App";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  LayoutDashboard,
  Mic,
  History,
  Phone,
  FileText,
  Settings,
  LogOut,
  Sun,
  Moon,
  Menu,
  X,
  Users,
  Trophy,
  BarChart3,
  Code2,
  Shield,
  HelpCircle,
  Building2,
} from "lucide-react";
import { useState } from "react";
import { resetTour } from "@/components/GuidedTour";

const navItems = [
  { path: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { path: "/roleplay/new", label: "New Roleplay", icon: Mic },
  { path: "/analyze", label: "Call Analysis", icon: Phone },
  { path: "/scripts", label: "Script Analysis", icon: FileText },
  { path: "/leaderboard", label: "Leaderboard", icon: Trophy },
  { path: "/analytics", label: "Analytics", icon: BarChart3 },
  { path: "/objection-handler", label: "Objection Coach", icon: Shield },
  { path: "/history", label: "History", icon: History },
  { path: "/team", label: "Team", icon: Users },
  { path: "/admin/teams", label: "Teams", icon: Building2, adminOnly: true },
  { path: "/embeds", label: "Embeds", icon: Code2, managerOnly: true },
  { path: "/settings", label: "Settings", icon: Settings },
];

export default function Layout({ children, hideNav = false }) {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  if (hideNav) {
    return (
      <div className="min-h-screen bg-background">
        {children}
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex">
      {/* Desktop Sidebar */}
      <aside className="hidden lg:flex flex-col w-64 border-r border-border bg-card">
        <div className="p-6 border-b border-border">
          <Link to="/dashboard" className="flex items-center gap-3">
            <img
              src={theme === "dark" ? "/logo-light.png" : "/logo-dark.png"}
              alt="Great Marketing LLC"
              className="h-10 w-auto"
            />
          </Link>
        </div>

        <nav className="flex-1 p-4 space-y-1">
          {navItems
            .filter(item => {
              if (item.adminOnly && !user?.is_admin) return false;
              if (item.managerOnly && !user?.is_manager && !user?.is_admin) return false;
              return true;
            })
            .map((item) => {
            const isActive = location.pathname === item.path || 
              (item.path === "/roleplay/new" && location.pathname.startsWith("/roleplay")) ||
              (item.path === "/scripts" && location.pathname.startsWith("/scripts")) ||
              (item.path === "/analyze" && location.pathname.startsWith("/analyze"));
            return (
              <Link
                key={item.path}
                to={item.path}
                data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, "-")}`}
                className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${
                  isActive
                    ? "bg-primary text-primary-foreground shadow-lg"
                    : "text-muted-foreground hover:bg-muted hover:text-foreground"
                }`}
              >
                <item.icon className="w-5 h-5" />
                <span className="font-medium">{item.label}</span>
              </Link>
            );
          })}
        </nav>

        <div className="p-4 border-t border-border">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="w-full justify-start gap-3 px-4" data-testid="user-menu-trigger">
                <Avatar className="w-8 h-8">
                  <AvatarImage src={user?.picture} alt={user?.name} />
                  <AvatarFallback className="bg-primary text-primary-foreground text-sm">
                    {user?.name?.charAt(0) || "U"}
                  </AvatarFallback>
                </Avatar>
                <div className="text-left">
                  <p className="text-sm font-medium truncate max-w-[140px]">{user?.name}</p>
                  <p className="text-xs text-muted-foreground truncate max-w-[140px]">{user?.email}</p>
                </div>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuItem onClick={() => { resetTour(); navigate("/dashboard"); }} data-testid="restart-tour-btn">
                <HelpCircle className="w-4 h-4 mr-2" /> Take a Tour
              </DropdownMenuItem>
              <DropdownMenuItem onClick={toggleTheme} data-testid="theme-toggle">
                {theme === "dark" ? (
                  <>
                    <Sun className="w-4 h-4 mr-2" /> Light Mode
                  </>
                ) : (
                  <>
                    <Moon className="w-4 h-4 mr-2" /> Dark Mode
                  </>
                )}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleLogout} className="text-destructive" data-testid="logout-btn">
                <LogOut className="w-4 h-4 mr-2" /> Sign Out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </aside>

      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-card border-b border-border">
        <div className="flex items-center justify-between p-4">
          <Link to="/dashboard" className="flex items-center gap-2">
            <img
              src={theme === "dark" ? "/logo-light.png" : "/logo-dark.png"}
              alt="Great Marketing LLC"
              className="h-8 w-auto"
            />
          </Link>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-toggle"
          >
            {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </Button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="border-t border-border bg-card">
            <nav className="p-4 space-y-1">
              {navItems
                .filter(item => {
                  if (item.adminOnly && !user?.is_admin) return false;
                  if (item.managerOnly && !user?.is_manager && !user?.is_admin) return false;
                  return true;
                })
                .map((item) => {
                const isActive = location.pathname === item.path;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setMobileMenuOpen(false)}
                    className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${
                      isActive
                        ? "bg-primary text-primary-foreground"
                        : "text-muted-foreground hover:bg-muted"
                    }`}
                  >
                    <item.icon className="w-5 h-5" />
                    <span>{item.label}</span>
                  </Link>
                );
              })}
              <div className="pt-4 border-t border-border mt-4 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Avatar className="w-8 h-8">
                    <AvatarImage src={user?.picture} alt={user?.name} />
                    <AvatarFallback className="bg-primary text-primary-foreground text-sm">
                      {user?.name?.charAt(0)}
                    </AvatarFallback>
                  </Avatar>
                  <span className="text-sm font-medium">{user?.name}</span>
                </div>
                <div className="flex gap-2">
                  <Button variant="ghost" size="icon" onClick={toggleTheme}>
                    {theme === "dark" ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
                  </Button>
                  <Button variant="ghost" size="icon" onClick={handleLogout}>
                    <LogOut className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </nav>
          </div>
        )}
      </div>

      {/* Main Content */}
      <main className="flex-1 lg:p-8 p-4 pt-20 lg:pt-8 overflow-auto">
        {children}
      </main>
    </div>
  );
}
